import type { ConnectorParameterUndoPlan } from '../../types/parameterSetupUndo';
import { hasConnectorUndoWork, UnusableUndoPlanMessage } from './undoPlanExecution';

export const AppliedSetupMessage = 'Status: Parameter setup was applied for this project.';

const UndoFailedRetryHint = 'Retry if you still want to revert the parameter setup.';

/** What a setup step may say and offer about the parameters it already applied. */
export interface UndoPlanAffordance {
  /** Whether the applied-setup line belongs on screen at all. */
  visible: boolean;
  /** Whether the revert button may be offered — never for a record that cannot run. */
  canRevert: boolean;
  message: string;
  severity: 'success' | 'warning' | 'error';
}

const Hidden: UndoPlanAffordance = {
  visible: false,
  canRevert: false,
  message: '',
  severity: 'success',
};

const Damaged: UndoPlanAffordance = {
  visible: true,
  canRevert: false,
  message: UnusableUndoPlanMessage,
  severity: 'error',
};

/**
 * Decides what a step shows about a recorded parameter setup. A revert is only
 * ever offered for a record that can still send something to Revit: a button
 * that cannot revert anything reads as a working escape hatch and leaves the
 * user waiting for a change that never happens.
 */
export const describeUndoPlanAffordance = (
  plan: ConnectorParameterUndoPlan | null | undefined,
): UndoPlanAffordance => {
  if (!plan || plan.status === 'undone') {
    return Hidden;
  }

  if (plan.status === 'unusable') {
    return { ...Damaged, message: plan.error || UnusableUndoPlanMessage };
  }

  if (!hasConnectorUndoWork(plan)) {
    return Damaged;
  }

  if (plan.status === 'undo-failed') {
    return {
      visible: true,
      canRevert: true,
      message: `Status: Undo failed. ${plan.error || UndoFailedRetryHint}`,
      severity: 'warning',
    };
  }

  return {
    visible: true,
    canRevert: true,
    message: AppliedSetupMessage,
    severity: 'success',
  };
};
